import React, { useEffect, useState } from 'react';
import { Cpu, Search, Loader2, Plus, Pencil, Trash2, History, Settings2, ArrowRightLeft, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { STATUS_META, LINES } from '../mock/data';
import { PageHeader, Modal, Field, HistoryModal } from '../components/common';
import { useI18n } from '../i18n/I18nContext';
import { useAuth } from '../context/AuthContext';
import { useWarehouse } from '../context/WarehouseContext';
import { useRealtime } from '../realtime/RealtimeContext';
import api from '../api/client';

const EMPTY = { id: '', displayName: '', type: 'CNC Mill', line: 'Line A', status: 'IDLE', operator: '', oee: 70, utilization: 50, downtimeTargetHours: 0, settingTargetMinutes: 0 };

export default function Machines() {
  const { t } = useI18n();
  const { user } = useAuth();
  const wh = useWarehouse();
  const [machines, setMachines] = useState([]);
  const [types, setTypes] = useState([]);
  const [manageTypes, setManageTypes] = useState(false);
  const [newType, setNewType] = useState('');
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [modal, setModal] = useState(null); // {mode:'create'|'edit', data}
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState(null); // machine id for history modal
  const [moveIds, setMoveIds] = useState(null); // array of machine ids being moved (null = closed)
  const [moveTarget, setMoveTarget] = useState('');
  const [moving, setMoving] = useState(false);
  const [preflight, setPreflight] = useState([]); // per-machine warnings
  const [loadingPre, setLoadingPre] = useState(false);
  const [sel, setSel] = useState(() => new Set()); // bulk selection

  const isAdmin = user?.role === 'ADMIN';
  const otherWarehouses = (wh?.warehouses || []).filter((w) => w.id !== wh?.activeId);

  const load = () => api.listMachines().then(setMachines).catch(() => {}).finally(() => setLoading(false));
  const loadTypes = () => api.listMachineTypes().then(setTypes).catch(() => {});
  useEffect(() => { load(); loadTypes(); }, []);
  useRealtime(() => { load(); loadTypes(); });

  const toggleSel = (id) => setSel((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const openMove = (m) => { setMoveIds([m.id]); setMoveTarget(''); };
  const openBulkMove = () => { setMoveIds([...sel]); setMoveTarget(''); };

  useEffect(() => {
    if (!moveIds || moveIds.length === 0) { setPreflight([]); return; }
    setLoadingPre(true);
    api.movePreflight(moveIds).then(setPreflight).catch(() => setPreflight([])).finally(() => setLoadingPre(false));
  }, [moveIds]);

  const warnCount = preflight.filter((p) => p.openProblems > 0 || p.runningJobs > 0).length;

  const doMove = async () => {
    if (!moveTarget) { toast.error('Choose a destination factory'); return; }
    setMoving(true);
    try {
      const dest = wh?.warehouses.find((w) => w.id === moveTarget)?.name || moveTarget;
      if (moveIds.length === 1) {
        const r = await api.moveMachine(moveIds[0], moveTarget);
        const total = Object.values(r.moved || {}).reduce((s, v) => s + v, 0);
        toast.success(`${moveIds[0]} moved to ${dest}`, { description: `${total} records transferred.` });
      } else {
        const r = await api.moveMachines(moveIds, moveTarget);
        toast.success(`${r.moved}/${r.total} machines moved to ${dest}`, {
          description: r.moved < r.total ? 'Some were skipped — see machine list.' : 'All data transferred.',
        });
      }
      setMoveIds(null); setSel(new Set()); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Move failed');
    } finally { setMoving(false); }
  };

  const addType = async () => {
    const name = newType.trim();
    if (!name) return;
    try { const created = await api.createMachineType(name); setNewType(''); loadTypes(); setForm((f) => ({ ...f, type: created.name })); toast.success(`Type "${name}" added`); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add'); }
  };
  const removeType = async (tp) => {
    try { await api.deleteMachineType(tp.id); loadTypes(); }
    catch (e) { toast.error('Delete failed'); }
  };

  const rows = machines.filter((m) => !query || m.id.toLowerCase().includes(query.toLowerCase()) || m.displayName.toLowerCase().includes(query.toLowerCase()));

  const openCreate = () => { setForm(EMPTY); setModal({ mode: 'create' }); };
  const openEdit = (m) => { setForm({ ...m }); setModal({ mode: 'edit' }); };

  const save = async () => {
    setSaving(true);
    try {
      if (modal.mode === 'create') {
        const created = await api.createMachine(form);
        setMachines((p) => [...p, created]);
        toast.success(`Machine ${created.id} added`);
      } else {
        const upd = await api.updateMachine(form.id, form);
        setMachines((p) => p.map((x) => (x.id === upd.id ? upd : x)));
        toast.success(`Machine ${upd.id} updated`);
      }
      setModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (m) => {
    setMachines((p) => p.filter((x) => x.id !== m.id));
    try { await api.deleteMachine(m.id); toast.success(`${m.id} deleted`); }
    catch (e) { toast.error('Delete failed'); load(); }
  };

  const num = (v) => (v === '' ? '' : Number(v));

  return (
    <div className="kmt-fade-in">
      <PageHeader icon={Cpu} title="Machine Registry" subtitle={`${machines.length} machines configured`}
        right={<button className="kmt-btn-gold flex items-center gap-2" onClick={openCreate}><Plus size={16} />Add Machine</button>} />
      <div className="p-8 space-y-5">
        <div className="relative max-w-md">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: 'var(--kmt-muted-2)' }} />
          <input className="kmt-input" style={{ paddingLeft: 40 }} placeholder={t('common.search') + '…'} value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        {isAdmin && sel.size > 0 && (
          <div className="kmt-panel px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="bulk-move-bar" style={{ borderColor: 'rgba(240,169,43,0.4)' }}>
            <span className="text-sm font-medium">{sel.size} machine{sel.size > 1 ? 's' : ''} selected</span>
            <div className="flex-1" />
            <button className="kmt-btn-gold flex items-center gap-2" onClick={openBulkMove} data-testid="bulk-move-btn"><ArrowRightLeft size={15} />Move to factory…</button>
            <button className="kmt-btn-ghost" onClick={() => setSel(new Set())} data-testid="bulk-clear-btn">Clear</button>
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center py-24" style={{ color: 'var(--kmt-muted)' }}><Loader2 className="animate-spin" size={28} /></div>
        ) : (
          <div className="kmt-panel overflow-x-auto">
            <table className="w-full kmt-table">
              <thead><tr>
                {isAdmin && <th className="kmt-th" style={{ width: 36 }}><input type="checkbox" data-testid="select-all-machines"
                  checked={rows.length > 0 && rows.every((m) => sel.has(m.id))}
                  onChange={(e) => setSel(e.target.checked ? new Set(rows.map((m) => m.id)) : new Set())} /></th>}
                <th className="kmt-th">ID</th><th className="kmt-th">Name</th><th className="kmt-th">Type</th><th className="kmt-th">Line</th><th className="kmt-th">Operator</th><th className="kmt-th">OEE</th><th className="kmt-th">Utilization</th><th className="kmt-th">Status</th><th className="kmt-th">Actions</th>
              </tr></thead>
              <tbody>
                {rows.map((m) => {
                  const meta = STATUS_META[m.status] || STATUS_META.IDLE;
                  return (
                    <tr key={m.id}>
                      {isAdmin && <td className="kmt-td"><input type="checkbox" checked={sel.has(m.id)} onChange={() => toggleSel(m.id)} data-testid={`select-machine-${m.id}`} /></td>}
                      <td className="kmt-td font-mono-k" data-label="ID" style={{ color: 'var(--kmt-gold)' }}>{m.id}</td>
                      <td className="kmt-td" data-label="Name">{m.displayName}</td>
                      <td className="kmt-td" data-label="Type" style={{ color: 'var(--kmt-muted)' }}>{m.type}</td>
                      <td className="kmt-td" data-label="Line">{m.line}</td>
                      <td className="kmt-td" data-label="Operator">{m.operator}</td>
                      <td className="kmt-td font-mono-k" data-label="OEE">{m.oee}%</td>
                      <td className="kmt-td" data-label="Utilization">
                        <div className="flex items-center gap-2">
                          <div className="h-1.5 w-20 rounded-full" style={{ background: '#0e0e10' }}><div className="h-1.5 rounded-full" style={{ width: `${m.utilization}%`, background: 'var(--kmt-gold)' }} /></div>
                          <span className="font-mono-k text-xs" style={{ color: 'var(--kmt-muted)' }}>{m.utilization}%</span>
                        </div>
                      </td>
                      <td className="kmt-td" data-label="Status"><span className={`kmt-badge ${meta.cls}`}><span className={`kmt-dot ${meta.dot}`} />{t(`status.${m.status.toLowerCase()}`)}</span></td>
                      <td className="kmt-td" data-label="Actions">
                        <div className="flex gap-2">
                          <button onClick={() => setHistory(m.id)} title="History" style={{ color: 'var(--kmt-muted)' }} className="hover:text-[var(--kmt-blue)]"><History size={15} /></button>
                          {isAdmin && <button onClick={() => openMove(m)} title="Move to another factory" data-testid={`machine-move-${m.id}`} style={{ color: 'var(--kmt-muted)' }} className="hover:text-[var(--kmt-gold)]"><ArrowRightLeft size={15} /></button>}
                          <button onClick={() => openEdit(m)} style={{ color: 'var(--kmt-muted)' }} className="hover:text-[var(--kmt-gold)]"><Pencil size={15} /></button>
                          <button onClick={() => remove(m)} style={{ color: 'var(--kmt-muted)' }} className="hover:text-[var(--kmt-red)]"><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal open={!!modal} onClose={() => setModal(null)}
        title={modal?.mode === 'create' ? 'Add Machine' : `Edit ${form.id}`}
        footer={<>
          <button className="kmt-btn-ghost" onClick={() => setModal(null)}>Cancel</button>
          <button className="kmt-btn-gold" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
        </>}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Machine ID">
            <input className="kmt-input" value={form.id} disabled={modal?.mode === 'edit'} placeholder="M52"
              onChange={(e) => setForm({ ...form, id: e.target.value })} />
          </Field>
          <Field label="Display Name">
            <input className="kmt-input" value={form.displayName} onChange={(e) => setForm({ ...form, displayName: e.target.value })} />
          </Field>
          <Field label="Type">
            <div className="flex items-center justify-between mb-1">
              <span />
              <button type="button" className="flex items-center gap-1 text-xs" onClick={() => setManageTypes((s) => !s)} data-testid="manage-types-toggle" style={{ color: 'var(--kmt-muted)' }}><Settings2 size={12} />Manage</button>
            </div>
            <select className="kmt-input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} data-testid="machine-type-select">
              {!types.find((x) => x.name === form.type) && form.type && <option value={form.type}>{form.type}</option>}
              {types.map((x) => <option key={x.id} value={x.name}>{x.name}</option>)}
            </select>
            {manageTypes && (
              <div className="mt-2 p-3 rounded-lg" style={{ background: 'var(--kmt-panel-2)', border: '1px solid var(--kmt-border)' }} data-testid="type-manager">
                <div className="flex gap-2">
                  <input className="kmt-input flex-1" placeholder="New type…" value={newType} onChange={(e) => setNewType(e.target.value)} data-testid="new-type-input" />
                  <button type="button" className="kmt-btn-gold flex items-center gap-1" onClick={addType} data-testid="new-type-add"><Plus size={14} /></button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {types.map((tp) => (
                    <span key={tp.id} className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs" style={{ background: 'var(--kmt-panel)', border: '1px solid var(--kmt-border)' }}>
                      {tp.name}
                      <button type="button" onClick={() => removeType(tp)} data-testid={`type-delete-${tp.id}`} style={{ color: 'var(--kmt-muted-2)' }} className="hover:text-[var(--kmt-red)]"><Trash2 size={11} /></button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </Field>
          <Field label="Line">
            <select className="kmt-input" value={form.line} onChange={(e) => setForm({ ...form, line: e.target.value })}>
              {LINES.map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </Field>
          <Field label="Operator">
            <input className="kmt-input" value={form.operator} onChange={(e) => setForm({ ...form, operator: e.target.value })} />
          </Field>
          <Field label="Status">
            <select className="kmt-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {Object.keys(STATUS_META).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="OEE (%)">
            <input className="kmt-input" type="number" value={form.oee} onChange={(e) => setForm({ ...form, oee: num(e.target.value) })} />
          </Field>
          <Field label="Utilization (%)">
            <input className="kmt-input" type="number" value={form.utilization} onChange={(e) => setForm({ ...form, utilization: num(e.target.value) })} />
          </Field>
          <Field label="Monthly Downtime Target (hours)">
            <input className="kmt-input" type="number" min="0" step="0.5" value={form.downtimeTargetHours ?? 0} onChange={(e) => setForm({ ...form, downtimeTargetHours: Math.max(0, parseFloat(e.target.value || '0')) })} data-testid="machine-downtime-target-input" />
          </Field>
          <Field label="Changeover Target (minutes)">
            <input className="kmt-input" type="number" min="0" step="1" value={form.settingTargetMinutes ?? 0} onChange={(e) => setForm({ ...form, settingTargetMinutes: Math.max(0, parseInt(e.target.value || '0', 10)) })} data-testid="machine-setting-target-input" />
          </Field>
        </div>
      </Modal>

      <HistoryModal open={!!history} onClose={() => setHistory(null)} entityType="machine" entityId={history} api={api} />

      <Modal open={!!moveIds} onClose={() => setMoveIds(null)}
        title={moveIds && moveIds.length === 1 ? `Move ${moveIds[0]} to another factory` : `Move ${moveIds?.length || 0} machines to another factory`}
        footer={<>
          <button className="kmt-btn-ghost" onClick={() => setMoveIds(null)}>Cancel</button>
          <button className="kmt-btn-gold" disabled={moving || !moveTarget} onClick={doMove} data-testid="machine-move-confirm">{moving ? 'Moving…' : (warnCount > 0 ? 'Move Anyway' : 'Move Machine')}</button>
        </>}>
        <div className="space-y-4">
          <div className="text-sm" style={{ color: 'var(--kmt-muted)' }}>
            The selected machine{moveIds && moveIds.length > 1 ? 's' : ''} and all associated data — jobs, problems, machine logs, downtime and material usage — will be transferred to the chosen factory.
          </div>

          {loadingPre ? (
            <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--kmt-muted)' }}><Loader2 className="animate-spin" size={15} />Checking machine status…</div>
          ) : warnCount > 0 && (
            <div className="rounded-lg p-3 space-y-2" data-testid="move-warning" style={{ background: 'rgba(240,169,43,0.08)', border: '1px solid rgba(240,169,43,0.45)' }}>
              <div className="flex items-center gap-2 text-sm font-medium" style={{ color: 'var(--kmt-amber)' }}>
                <AlertTriangle size={15} />Warning — active work on {warnCount} machine{warnCount > 1 ? 's' : ''}
              </div>
              {preflight.filter((p) => p.openProblems > 0 || p.runningJobs > 0).map((p) => (
                <div key={p.machine_id} className="text-xs flex flex-wrap items-center gap-2" style={{ color: 'var(--kmt-muted)' }}>
                  <span className="font-mono-k" style={{ color: 'var(--kmt-gold)' }}>{p.machine_id}</span>
                  {p.openProblems > 0 && <span className="kmt-badge s-problem" style={{ fontSize: 9 }}>{p.openProblems} open problem{p.openProblems > 1 ? 's' : ''}</span>}
                  {p.runningJobs > 0 && <span className="kmt-badge s-progress" style={{ fontSize: 9 }}>{p.runningJobs} running job{p.runningJobs > 1 ? 's' : ''}</span>}
                </div>
              ))}
              <div className="text-xs" style={{ color: 'var(--kmt-muted)' }}>Moving now will carry this open work into the new factory. Resolve it first if that's not intended.</div>
            </div>
          )}

          <Field label="Destination Factory">
            <select className="kmt-input" value={moveTarget} onChange={(e) => setMoveTarget(e.target.value)} data-testid="machine-move-target">
              <option value="">— Select factory —</option>
              {otherWarehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </Field>
          {otherWarehouses.length === 0 && (
            <div className="text-xs" style={{ color: 'var(--kmt-red)' }}>No other factory available. Create one in Settings → Factories / Warehouses first.</div>
          )}
        </div>
      </Modal>
    </div>
  );
}
